import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { photoService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import PhotoUpload from '../../components/PhotoUpload/PhotoUpload';
import PhotoLightbox from '../../components/PhotoLightbox/PhotoLightbox';
import { FiImage, FiPlus, FiFolder, FiGrid, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import './Photos.css';

const Photos = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('albums');
  const [photos, setPhotos] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showCreateAlbum, setShowCreateAlbum] = useState(false);
  const [newAlbumName, setNewAlbumName] = useState('');
  const [newAlbumDescription, setNewAlbumDescription] = useState('');
  const [creatingAlbum, setCreatingAlbum] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [sortAsc, setSortAsc] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPhotos = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const response = await photoService.getMyPhotos({ pageNumber: pageNum, pageSize: 40 });
      if (response.success) {
        const photoData = Array.isArray(response.data)
          ? response.data
          : (response.data.photos || response.data.items || []);

        if (append) {
          setPhotos(prev => [...prev, ...photoData]);
        } else {
          setPhotos(photoData);
        }

        const totalCount = response.data.totalCount || photoData.length;
        const currentCount = append ? photos.length + photoData.length : photoData.length;
        setHasMore(currentCount < totalCount);
      }
    } catch (err) {
      console.error('Failed to fetch photos:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [photos.length]);

  const fetchAlbums = useCallback(async () => {
    try {
      const response = await photoService.getMyAlbums();
      if (response.success) {
        const albumData = Array.isArray(response.data)
          ? response.data
          : (response.data.albums || response.data.items || []);
        setAlbums(albumData);
      }
    } catch (err) {
      console.error('Failed to fetch albums:', err);
    }
  }, []);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPhotos(nextPage, true);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPhotos(), fetchAlbums()]);
      setLoading(false);
    };
    loadData();
  }, [fetchPhotos, fetchAlbums]);

  const handlePhotoUploaded = (newPhoto) => {
    setPhotos(prev => [newPhoto, ...prev]);
    setShowUpload(false);
  };

  const handleCreateAlbum = async (e) => {
    e.preventDefault();
    if (!newAlbumName.trim()) return;

    try {
      setCreatingAlbum(true);
      const response = await photoService.createAlbum({
        albumName: newAlbumName.trim(),
        description: newAlbumDescription.trim(),
        visibility: 'family'
      });

      if (response.success) {
        setAlbums(prev => [response.data, ...prev]);
        setNewAlbumName('');
        setNewAlbumDescription('');
        setShowCreateAlbum(false);
      }
    } catch (err) {
      console.error('Failed to create album:', err);
    } finally {
      setCreatingAlbum(false);
    }
  };

  const openLightbox = (photo, index) => {
    setLightboxPhoto(photo);
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxPhoto(null);
  };

  const navigateLightbox = (direction) => {
    const newIndex = lightboxIndex + direction;
    if (newIndex >= 0 && newIndex < photos.length) {
      setLightboxIndex(newIndex);
      setLightboxPhoto(sortedPhotos[newIndex]);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      const response = await photoService.deletePhoto(photoId);
      if (response.success) {
        setPhotos(prev => prev.filter(p => p.photoID !== photoId));
        closeLightbox();
      }
    } catch (err) {
      console.error('Failed to delete photo:', err);
      alert('Failed to delete photo. Please try again.');
    }
  };

  const sortedPhotos = [...photos].sort((a, b) => {
    const dateA = new Date(a.takenDate || a.createdDate);
    const dateB = new Date(b.takenDate || b.createdDate);
    return sortAsc ? dateA - dateB : dateB - dateA;
  });

  if (loading) {
    return (
      <div className="photos-page">
        <div className="container">
          <div className="loading-state">
            <span className="spinner spinner-lg"></span>
            <p>Loading photos...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="photos-page">
      <div className="container">
        {/* Header */}
        <div className="photos-header">
          <div className="page-header">
            <h1><FiImage className="page-header-icon" /> Photos</h1>
            <p className="page-subtitle">Share and view family memories</p>
          </div>
          <div className="photos-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => setSortAsc(!sortAsc)}>
              {sortAsc ? <><FiChevronUp /> Oldest First</> : <><FiChevronDown /> Newest First</>}
            </button>
            <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
              <FiPlus /> Upload Photo
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="photos-tabs">
          <button
            className={`photos-tab ${activeTab === 'photos' ? 'active' : ''}`}
            onClick={() => setActiveTab('photos')}
          >
            <FiGrid /> All Photos
          </button>
          <button
            className={`photos-tab ${activeTab === 'albums' ? 'active' : ''}`}
            onClick={() => setActiveTab('albums')}
          >
            <FiFolder /> Albums
          </button>
        </div>

        {/* Content */}
        {activeTab === 'photos' && (
          <div className="photos-content">
            {photos.length === 0 ? (
              <div className="empty-state card">
                <div className="empty-state-icon">📷</div>
                <h3 className="empty-state-title">No photos yet</h3>
                <p>Upload your first photo to get started!</p>
                <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
                  <FiPlus /> Upload Photo
                </button>
              </div>
            ) : (
              <>
                <div className="photos-grid">
                  {sortedPhotos.map((photo, index) => (
                    <div
                      key={photo.photoID}
                      className="photo-item"
                      onClick={() => openLightbox(photo, index)}
                    >
                      {photo.fileType === 'video' ? (
                      <>
                        {photo.thumbnailURL ? (
                          <img src={photo.thumbnailURL} alt={photo.caption || 'Video'} />
                        ) : (
                          <div className="video-tile-placeholder" />
                        )}
                        <div className="video-play-overlay">▶</div>
                      </>
                    ) : (
                      <img
                        src={photo.thumbnailURL || photo.fileURL}
                        alt={photo.caption || 'Photo'}
                      />
                    )}
                      {photo.caption && (
                        <div className="photo-caption">{photo.caption}</div>
                      )}
                    </div>
                  ))}
                </div>

                {hasMore && (
                  <div className="load-more">
                    <button
                      className="btn btn-secondary"
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                    >
                      {loadingMore ? (
                        <><span className="spinner spinner-sm"></span> Loading...</>
                      ) : 'Load More'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'albums' && (
          <div className="albums-content">
            <div className="albums-header">
              <button className="btn btn-secondary" onClick={() => setShowCreateAlbum(true)}>
                <FiPlus /> Create Album
              </button>
            </div>

            {albums.length === 0 ? (
              <div className="empty-state card">
                <div className="empty-state-icon">📁</div>
                <h3 className="empty-state-title">No albums yet</h3>
                <p>Create an album to organize your photos!</p>
                <button className="btn btn-primary" onClick={() => setShowCreateAlbum(true)}>
                  <FiPlus /> Create Album
                </button>
              </div>
            ) : (
              <div className="albums-grid">
                {albums.map(album => (
                  <Link
                    key={album.albumID}
                    to={`/photos/album/${album.albumID}`}
                    className="album-card card"
                  >
                    <div className="album-cover">
                      {album.coverPhotoURL ? (
                        <img src={album.coverPhotoURL} alt={album.albumName} />
                      ) : (
                        <div className="album-cover-placeholder">
                          <FiFolder />
                        </div>
                      )}
                    </div>
                    <div className="album-info">
                      {album.eventID && (
                        <span className="album-event-label">Event</span>
                      )}
                      <h3 className="album-name">{album.albumName}</h3>
                      <p className="album-count">{album.photoCount || 0} photos</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <PhotoUpload
          onClose={() => setShowUpload(false)}
          onUploaded={handlePhotoUploaded}
          albums={albums}
        />
      )}

      {/* Create Album Modal */}
      {showCreateAlbum && (
        <div className="modal-overlay" onClick={() => setShowCreateAlbum(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Create Album</h2>
            <form onSubmit={handleCreateAlbum}>
              <div className="form-group">
                <label className="form-label">Album Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., Summer Vacation 2024"
                  value={newAlbumName}
                  onChange={(e) => setNewAlbumName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description (optional)</label>
                <textarea
                  className="form-input"
                  placeholder="What's this album about?"
                  rows={3}
                  value={newAlbumDescription}
                  onChange={(e) => setNewAlbumDescription(e.target.value)}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowCreateAlbum(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={creatingAlbum || !newAlbumName.trim()}>
                  {creatingAlbum ? 'Creating...' : 'Create Album'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxPhoto && (
        <PhotoLightbox
          photo={lightboxPhoto}
          onClose={closeLightbox}
          onPrev={() => navigateLightbox(-1)}
          onNext={() => navigateLightbox(1)}
          hasPrev={lightboxIndex > 0}
          hasNext={lightboxIndex < sortedPhotos.length - 1}
          onDelete={lightboxPhoto.memberID === user?.memberID || user?.isAdmin ? handleDeletePhoto : null}
          onCaptionUpdated={(photoId, newCaption) => {
            setPhotos(prev => prev.map(p => p.photoID === photoId ? { ...p, caption: newCaption } : p));
            setLightboxPhoto(prev => ({ ...prev, caption: newCaption }));
          }}
        />
      )}
    </div>
  );
};

export default Photos;
